/**
 * Seamless-loop preparation, pure TypeScript on Float32Array so the same code runs in the offline pipeline,
 * on the server for uploads, and in the browser for recorded samples.
 *
 * Method (PLAN.md §3.4): pick a loop start S in the sustain, search the loop end E maximising the normalised
 * cross-correlation of the waveform around S and around E (so the seam is phase-aligned), prefer an E where the
 * RMS matches, snap to same-slope zero crossings, then fold the pre-roll before S into the tail before E with a
 * long raised-cosine crossfade so the buffer is continuous across the wrap.
 */

export interface LoopOptions {
  /** earliest loop start (s). Default: 30% of the file, clamped to [0.25, 4] s */
  startSec?: number;
  minLoopSec?: number;   // default 1.2
  maxLoopSec?: number;   // default 8
  windowSec?: number;    // correlation window, default 0.04
  guardSec?: number;     // audio kept after E, default 0.25
  crossfadeFraction?: number; // of loop length, default 0.4 (capped by pre-roll)
  /** 0..1: how much to prefer longer loops when seams are comparably good (0 = pure seam quality). Default 0. */
  lengthBias?: number;
}

export interface LoopResult {
  start: number;        // frames
  end: number;          // frames (exclusive)
  correlation: number;  // -1..1 at the chosen seam
  crossfade: number;    // frames of raised-cosine fold applied before `end`
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

function ncc(a: Float32Array, ai: number, bi: number, w: number): number {
  let ma = 0, mb = 0;
  for (let i = 0; i < w; i++) { ma += a[ai + i]!; mb += a[bi + i]!; }
  ma /= w; mb /= w;
  let num = 0, da = 0, db = 0;
  for (let i = 0; i < w; i++) {
    const x = a[ai + i]! - ma, y = a[bi + i]! - mb;
    num += x * y; da += x * x; db += y * y;
  }
  const d = Math.sqrt(da * db);
  return d > 1e-12 ? num / d : 0;
}

function rms(a: Float32Array, from: number, w: number): number {
  let s = 0; for (let i = 0; i < w; i++) { const v = a[from + i]!; s += v * v; }
  return Math.sqrt(s / w);
}

function snapZeroCrossing(a: Float32Array, i: number, rising: boolean, maxSearch: number): number {
  for (let d = 0; d < maxSearch; d++) {
    for (const j of [i + d, i - d]) {
      if (j <= 0 || j >= a.length - 1) continue;
      const p = a[j - 1]!, c = a[j]!;
      if (rising ? p <= 0 && c > 0 : p >= 0 && c < 0) return j;
    }
  }
  return i;
}

/** Find loop points on a mono mixdown. */
export function findLoop(mono: Float32Array, sampleRate: number, opts: LoopOptions = {}): LoopResult {
  const n = mono.length;
  const w = Math.max(64, Math.floor((opts.windowSec ?? 0.04) * sampleRate));
  const guard = Math.floor((opts.guardSec ?? 0.25) * sampleRate);
  const minLoop = Math.floor((opts.minLoopSec ?? 1.2) * sampleRate);
  const maxLoop = Math.floor((opts.maxLoopSec ?? 8) * sampleRate);
  let S = Math.floor((opts.startSec ?? clamp(0.3 * (n / sampleRate), 0.25, 4)) * sampleRate);
  // make sure there is room for a loop after S
  if (S + minLoop + w + guard > n) S = Math.max(0, n - minLoop - w - guard);
  S = Math.max(S, 0);
  const rising = mono[Math.min(n - 1, S + 1)]! >= mono[S]!;
  S = snapZeroCrossing(mono, S, rising, w);
  const eMin = S + minLoop;
  const eMax = Math.min(n - w - guard, S + maxLoop);
  if (eMax <= eMin) {
    // very short file: loop the whole thing from S with whatever crossfade fits
    const end = Math.max(S + 1, n - guard);
    return { start: S, end, correlation: 0, crossfade: Math.min(S, Math.floor((end - S) * 0.4)) };
  }
  const rmsS = rms(mono, S, w);
  const bias = clamp(opts.lengthBias ?? 0, 0, 1);
  let best = -Infinity, bestE = eMin;
  const hop = 32;
  for (let e = eMin; e < eMax; e += hop) {
    const c = ncc(mono, S, e, w);
    const r = rms(mono, e, w);
    const pen = Math.abs(r - rmsS) / (rmsS + 1e-6);
    // a longer loop repeats less audibly; with lengthBias > 0 a somewhat weaker seam further out can win
    const score = c - 0.5 * pen + bias * ((e - eMin) / Math.max(1, eMax - eMin));
    if (score > best) { best = score; bestE = e; }
  }
  // refine around the coarse optimum
  let fineBest = -Infinity, E = bestE;
  for (let e = Math.max(eMin, bestE - hop); e < Math.min(eMax, bestE + hop); e++) {
    const c = ncc(mono, S, e, w);
    if (c > fineBest) { fineBest = c; E = e; }
  }
  E = snapZeroCrossing(mono, E, rising, 64);
  const loopLen = E - S;
  const crossfade = Math.min(S, Math.floor(loopLen * (opts.crossfadeFraction ?? 0.4)));
  return { start: S, end: E, correlation: fineBest, crossfade };
}

/**
 * Fold the pre-roll before `start` into the tail before `end` with a raised-cosine crossfade (in place on copies).
 * After baking, `AudioBufferSourceNode.loop = true; loopStart = start/sr; loopEnd = end/sr` is seamless.
 * Returns new channel arrays trimmed to [start - crossfade, end + guard] plus the adjusted loop points.
 */
export function bakeLoop(channels: Float32Array[], loop: LoopResult, guardFrames: number): { channels: Float32Array[]; loopStart: number; loopEnd: number } {
  const X = loop.crossfade;
  const from = Math.max(0, loop.start - X);
  const to = Math.min(channels[0]!.length, loop.end + guardFrames);
  const out = channels.map((ch) => ch.slice(from, to));
  const S = loop.start - from, E = loop.end - from;
  for (const ch of out) {
    for (let i = 0; i < X; i++) {
      const t = i / X;
      const a = Math.sin((Math.PI / 2) * t) ** 2;       // fade-in of the pre-roll
      const b = 1 - a;                                    // fade-out of the tail (correlated → constant sum)
      const tail = E - X + i, pre = S - X + i;
      ch[tail] = ch[tail]! * b + ch[pre]! * a;
    }
  }
  return { channels: out, loopStart: S, loopEnd: E };
}

/** Mono mixdown helper. */
export function mixToMono(channels: Float32Array[]): Float32Array {
  const n = channels[0]!.length, out = new Float32Array(n), k = 1 / channels.length;
  for (const ch of channels) for (let i = 0; i < n; i++) out[i]! += ch[i]! * k;
  return out;
}

/** Cheap fundamental estimate by autocorrelation on a sustain segment (Hz), or 0 if unvoiced. */
export function estimateF0(mono: Float32Array, sampleRate: number, minHz = 40, maxHz = 1200): number {
  const start = Math.floor(mono.length * 0.3);
  const len = Math.min(Math.floor(sampleRate * 0.5), mono.length - start);
  if (len < sampleRate * 0.05) return 0;
  const seg = mono.subarray(start, start + len);
  let mean = 0; for (let i = 0; i < len; i++) mean += seg[i]!; mean /= len;
  const lo = Math.floor(sampleRate / maxHz), hi = Math.min(len - 1, Math.ceil(sampleRate / minHz));
  let e0 = 0; for (let i = 0; i < len; i++) { const v = seg[i]! - mean; e0 += v * v; }
  if (e0 < 1e-9) return 0;
  let bestLag = 0, bestR = 0;
  for (let lag = lo; lag <= hi; lag++) {
    let r = 0; for (let i = 0; i + lag < len; i++) r += (seg[i]! - mean) * (seg[i + lag]! - mean);
    r /= e0;
    if (r > bestR) { bestR = r; bestLag = lag; }
  }
  if (bestR < 0.3 || bestLag === 0) return 0;
  // prefer the lowest lag that is a sub-multiple with nearly the same correlation (avoid octave errors upward)
  for (const div of [4, 3, 2]) {
    const lag = Math.round(bestLag / div);
    if (lag < lo) continue;
    let r = 0; for (let i = 0; i + lag < len; i++) r += (seg[i]! - mean) * (seg[i + lag]! - mean);
    r /= e0;
    if (r > bestR * 0.9) { bestLag = lag; bestR = r; break; }
  }
  return sampleRate / bestLag;
}
